import { useEffect, useState } from 'react';
import { login, type Session } from '../lib/session';

function ssoUrl(silent: boolean): string {
  const ret = window.location.pathname + window.location.search;
  return `/api/sso/start?${silent ? 'silent=1&' : ''}return=${encodeURIComponent(ret)}`;
}

export function Login({ onLogin, notice = '' }: { onLogin: (s: Session) => void; notice?: string }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // single sign-on through Authentik (auth.mo2.dk): tried silently once when the page opens
  const [sso, setSso] = useState<'checking' | 'on' | 'off'>('checking');
  const [ssoMsg, setSsoMsg] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('sso'); // set by the BFF when a sign-on attempt came back without a session
    if (result) {
      params.delete('sso');
      const qs = params.toString();
      window.history.replaceState(null, '', window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash);
      if (result === 'nomailbox') setSsoMsg('Your mo2 account has no mailbox here — sign in with your mail password.');
      else if (result !== 'login_required' && result !== 'off') setSsoMsg('Single sign-on did not work — sign in with your password.');
    }
    let cancelled = false;
    fetch('/api/sso/config')
      .then((r) => (r.ok ? r.json() : { enabled: false }))
      .then((c: { enabled?: boolean }) => {
        if (cancelled) return;
        const loggedOut = document.cookie.split('; ').some((c2) => c2 === 'zwf_nosso=1');
        if (c.enabled && !result && !loggedOut) {
          // already signed in at auth.mo2.dk → straight back here with a session, no password
          window.location.replace(ssoUrl(true));
          return;
        }
        setSso(c.enabled ? 'on' : 'off');
      })
      .catch(() => !cancelled && setSso('off'));
    return () => {
      cancelled = true;
    };
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      onLogin(await login(username, password));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setBusy(false);
    }
  }

  if (sso === 'checking') {
    return (
      <div className="login">
        <form>
          <h1>Mail</h1>
          <div className="notice">Signing in…</div>
        </form>
      </div>
    );
  }

  return (
    <div className="login">
      <form onSubmit={submit}>
        <h1>Mail</h1>
        {notice ? <div className="notice">{notice}</div> : null}
        {ssoMsg ? <div className="notice">{ssoMsg}</div> : null}
        {sso === 'on' ? (
          <>
            <a className="sso-btn" href={ssoUrl(false)}>
              Sign in with mo2 account
            </a>
            <div className="sso-or">or with your mail password</div>
          </>
        ) : null}
        <input
          type="text"
          placeholder="you@mo2.dk"
          autoComplete="username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoFocus={sso !== 'on'}
        />
        <input
          type="password"
          placeholder="Password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <div className="err">{error}</div>
        <button type="submit" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}
